// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import getAutoDeliveryCollection from "@guestsupply/App/Handlers/AutoDelivery/loadAutoDeliveryOrders";
import setAutoDeliveryOrdersType from "@guestsupply/App/Handlers/AutoDelivery/SetAutoDeliveryOrdersType";
import updateAutoDeliveryParameter from "@guestsupply/App/Handlers/AutoDelivery/UpdateAutoDeliveryParameterHandler";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { parseBoolean } from "@guestsupply/XDL/Utilities";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    autoDeliveryOrdersType: state.xAutoDelivery.autoDeliveryOrdersType,
});

const mapDispatchToProps = {
    getAutoDeliveryCollection,
    updateAutoDeliveryParameter,
    setAutoDeliveryOrdersType,
};

export interface AutoDeliveryActionsStyles {
    autoDeliveryActionsGridContainer?: GridContainerProps;
    autoDeliveryActionsGridItem?: GridItemProps;
    autoDeliveryTypeLabel?: TypographyProps;
    autoDeliveryTypeRadioButton?: FieldSetPresentationProps<RadioComponentProps>;
    autoDeliveryTypeRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    divider?: GridItemProps;
}
export const autoDeliveryActionsStyles: AutoDeliveryActionsStyles = {};
const styles = autoDeliveryActionsStyles;
type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const AutoDeliveryActions = ({
    autoDeliveryOrdersType,
    updateAutoDeliveryParameter,
    setAutoDeliveryOrdersType,
}: Props) => {
    const handleAutoDeliveryOrdersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const autoDeliveryType = parseBoolean(e.currentTarget.value);
        updateAutoDeliveryParameter({
            ongoing: autoDeliveryType,
            // XNG-CHANGES : Adding uniqueId to call the auto delivery API on radio click
            id: uniqueId(),
        });
        setAutoDeliveryOrdersType({ autoDeliveryOrdersType: autoDeliveryType });
    };

    return (
        <GridContainer {...styles.autoDeliveryActionsGridContainer}>
            <GridItem {...styles.autoDeliveryActionsGridItem}>
                <Typography {...styles.autoDeliveryTypeLabel} as="p">
                    {translate("You can manage your Guest Supply auto-delivery orders here")}
                </Typography>
                <RadioGroup
                    as="legend"
                    value={autoDeliveryOrdersType?.toString()}
                    onChangeHandler={handleAutoDeliveryOrdersChange}
                    {...styles.autoDeliveryTypeRadioGroup}
                >
                    <Radio
                        {...styles.autoDeliveryTypeRadioButton}
                        value="false"
                        data-test-selector="auto_delivery_upcoming_orders"
                    >
                        {translate("Auto-Delivery Orders in the next 7 Days")}
                    </Radio>
                    <Radio
                        {...styles.autoDeliveryTypeRadioButton}
                        value="true"
                        data-test-selector="auto_delivery_onGoing_orders"
                    >
                        {translate("All Auto-Deliveries")}
                    </Radio>
                </RadioGroup>
            </GridItem>
            <GridItem {...styles.divider} />
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AutoDeliveryActions),
    definition: {
        group: "Auto Delivery" as any,
        allowedContexts: ["AutoDeliveryPage"],
    },
};
export default widgetModule;
